import React, { useEffect, useState } from "react";

import "./style.css";
import { useSelector } from "react-redux";
import { getAllUsers, impersonateUser } from "../../utils/account";
import Textfield from "../InviteUserModal/Textfield";
import ErrorText from "../commonComponents/ErrorTextContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateToHumanReadable } from "../../utils/utils";
import { UNIFIED_DASHBOARD_URL } from "../../utils/envConfig";

export default function Teams() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDevelop = searchParams.get("develop") === "true";
  const globalReducer = useSelector((store: any) => store.globalReducer);
  const [teamData, setTeamsData] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [searchStr, setSearchStr] = useState("");
  const [impersonateTarget, setImpersonateTarget] = useState<any>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState("");
  const [tokenGenerated, setTokenGenerated] = useState(false);

  useEffect(() => {
    getTeamData();
  }, []);

  const getTeamData = () => {
    getAllUsers().then((res) => {
      console.log(res);
      if (res.users) {
        setTeamsData(res.users);
      }
    });
  };

  const totalUsers = teamData.length;
  let filteredTeamData = teamData;

  if (searchStr && searchStr.length) {
    const lowerSearch = searchStr.toLowerCase();

    filteredTeamData = teamData.filter((ws: any) => {
      if (
        (ws.name && ws.name.toLowerCase().indexOf(lowerSearch) > -1) ||
        (ws.email && ws.email.toLowerCase().indexOf(lowerSearch) > -1) ||
        (ws._id && ws._id.indexOf(lowerSearch) > -1)
      )
        return true;
      return false;
    });
  }

  const closePopup = () => {
    setImpersonateTarget(null);
    setImpersonateError("");
    setTokenGenerated(false);
  };

  const handleGenerateToken = () => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    setImpersonateError("");
    impersonateUser(impersonateTarget.email)
      .then(() => {
        setTokenGenerated(true);
      })
      .catch((err) => {
        setImpersonateError(
          (err && err.message) || "Failed to generate token. Please try again."
        );
      })
      .finally(() => {
        setImpersonating(false);
      });
  };

  const handleOpen = () => {
    if (!window.confirm(`Open CXO dashboard as ${impersonateTarget?.email}?`)) return;
    window.open(UNIFIED_DASHBOARD_URL || "/", "_blank");
    closePopup();
  };

  return (
    <>
      {/* Impersonate confirmation popup */}
      {impersonateTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closePopup}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-6 w-[420px] flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-[#211f26] text-[16px]">Login as User</p>
              <p className="text-[#84828e] text-[13px]">
                {tokenGenerated
                  ? "Token generated. Click Open to launch the dashboard as this user."
                  : "Generate a session token to log in as this user in a new tab."}
              </p>
            </div>

            <div className="bg-[#f2eff3] rounded-lg px-4 py-3 flex flex-col gap-0.5">
              <p className="text-[#84828e] text-[11px]">Email</p>
              <p className="text-[#211f26] text-[14px] font-medium break-all">{impersonateTarget.email}</p>
            </div>

            {tokenGenerated && (
              <div className="flex flex-row items-center gap-2 bg-[#e6f4ea] rounded-lg px-4 py-2">
                <span className="text-green-600 text-[13px] font-medium">Token generated successfully</span>
              </div>
            )}

            {impersonateError && (
              <p className="text-red-500 text-[13px]">{impersonateError}</p>
            )}

            <div className="flex flex-row gap-3 justify-end">
              <button
                className="px-4 py-2 rounded-lg text-[14px] text-[#211f26] border border-[#eae7ec] hover:bg-[#f2eff3] transition-colors"
                onClick={closePopup}
                disabled={impersonating}
              >
                Cancel
              </button>
              {tokenGenerated ? (
                <button
                  className="px-4 py-2 rounded-lg text-[14px] text-white bg-[#1a7f37] hover:bg-[#166d30] transition-colors"
                  onClick={handleOpen}
                >
                  Open
                </button>
              ) : (
                <button
                  className="px-4 py-2 rounded-lg text-[14px] text-white bg-[#211f26] hover:bg-[#3a383f] transition-colors disabled:opacity-50"
                  onClick={handleGenerateToken}
                  disabled={impersonating}
                >
                  {impersonating ? "Generating..." : "Generate Token"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start relative size-full">
        {/* Header */}
        <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
          <div className="box-border content-stretch flex flex-row gap-3 items-center justify-start p-0 relative shrink-0">
            <div className="flex flex-col font-['Geist:SemiBold',_sans-serif] font-semibold justify-center leading-[0] relative shrink-0 text-[#211f26] text-[24px] text-left text-nowrap tracking-[-0.24px]">
              <p className="adjustLetterSpacing leading-[30px] whitespace-pre font-semibold text-[18px]">
                Team
              </p>
            </div>

            <div className="bg-[#cadfff] box-border content-stretch flex flex-row gap-2 items-center justify-center px-2 py-1 relative rounded-lg shrink-0">
              <div className="flex flex-col font-['Geist:Medium',_sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[#211f26] text-[12px] text-center text-nowrap">
                <p className="block leading-[16px] whitespace-pre">
                  {totalUsers} Users
                </p>
              </div>
            </div>
          </div>

          <div className="box-border content-stretch flex flex-row items-start justify-start p-0 relative shrink-0">
            <div className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative shrink-0 text-[#fcfcfd] text-[14px] text-center text-nowrap">
              <Textfield
                // label="Email ID"
                value={searchStr}
                onChange={(value) => setSearchStr(value)}
                placeholder="rick@desara.ai, chris@desara.ai, mika@desara.ai,..."
                type="email"
              />
              {emailError && <ErrorText hasError={emailError}></ErrorText>}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[rgba(253,252,253,0.6)] border border-[#EAE7EC] relative rounded-2xl shrink-0 w-full">
          {filteredTeamData && filteredTeamData.length > 0 ? (
            <div className="box-border content-stretch flex flex-col items-start justify-start overflow-hidden p-0 relative w-full">
              {/* Table Header */}
              <div className="bg-[#f2eff3] h-10 relative shrink-0 w-full rounded-tr-[14px] rounded-tl-[14px]">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-5 h-10  items-center justify-start px-4 py-2 relative w-full">
                    <div className="box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start p-0 relative shrink-0 w-[88px]">
                      <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#84828e] text-[12px] text-left">
                        <p className="block leading-[16px]">UserId</p>
                      </div>
                    </div>
                    <div className="basis-0 box-border content-stretch flex flex-row gap-2 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#84828e] text-[12px] text-left">
                        <p className="block leading-[16px]">Email ID</p>
                      </div>
                    </div>
                    <div className="box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start p-0 relative shrink-0 w-40">
                      <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#84828e] text-[12px] text-left">
                        <p className="block leading-[16px]">Name</p>
                      </div>
                    </div>
                    <div className="box-border content-stretch flex flex-row gap-0.5 items-center justify-start p-0 relative shrink-0 w-[240px]">
                      <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#84828e] text-[12px] text-left">
                        <p className="block leading-[16px]">Signup On</p>
                      </div>
                    </div>
                    {isDevelop && (
                      <div className="box-border content-stretch flex flex-row gap-0.5 items-center justify-start p-0 relative shrink-0 w-[120px]">
                        <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#84828e] text-[12px] text-left">
                          <p className="block leading-[16px]">Action</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="absolute border-[#eae7ec] border-[0px_0px_1px] border-solid inset-0 pointer-events-none" />
              </div>

              {/* Table Rows */}

              {filteredTeamData.map((member: any, index) => {
                return (
                  <div
                    key={member.id}
                    className=" cursor-pointer box-border content-stretch flex flex-row gap-5 items-center justify-start px-0 py-1 relative shrink-0 w-full workspace-team-member-row"
                    onClick={() => {
                      navigate("/user/" + member._id + "/workspace");
                    }}
                  >
                    <div className="absolute border-[#f0f0f0] border-[0px_0px_1px] border-solid inset-0 pointer-events-none" />
                    <div className="basis-0 grow min-h-px min-w-px relative shrink-0">
                      <div className="flex flex-row items-center relative size-full">
                        <div className="box-border content-stretch flex flex-row gap-5 items-center justify-start p-[16px] relative w-full">
                          {/* User ID Column */}
                          <div className="basis-0 box-border content-stretch flex flex-row gap-2 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                            <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#211f26] text-[14px] text-left">
                              <p className="block leading-[18px] teams-table-cell">
                                {member._id}
                              </p>
                            </div>
                          </div>

                          {/* Email ID Column */}
                          <div className="basis-0 box-border content-stretch flex flex-row gap-2 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                            <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#211f26] text-[14px] text-left">
                              <p className="block leading-[18px] teams-table-cell">
                                {member.email}
                              </p>
                            </div>
                          </div>
                          {/* Name Column */}
                          <div className="box-border content-stretch flex flex-row gap-0.5 items-center grow justify-start p-0 relative shrink-0 w-40">
                            <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#211f26] text-[14px] text-left">
                              <p className="block leading-[18px] teams-table-cell">
                                {member.name || "-"}
                              </p>
                            </div>
                          </div>

                          {/* Signup On Column */}
                          <div className="box-border content-stretch flex flex-row gap-0.5 items-center justify-start p-0 relative shrink-0 w-[240px]">
                            <div className="basis-0 flex flex-col font-['Geist:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#211f26] text-[14px] text-left">
                              <p className="block leading-[18px] teams-table-cell">
                                {formatDateToHumanReadable(
                                  member.createdAt,
                                  true,
                                  true
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Action Column */}
                          {isDevelop && (
                            <div
                              className="box-border content-stretch flex flex-row gap-0.5 items-center justify-start p-0 relative shrink-0 w-[120px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-[#211f26] hover:bg-[#3a383f] transition-colors whitespace-nowrap"
                                onClick={() => {
                                  setImpersonateError("");
                                  setTokenGenerated(false);
                                  setImpersonateTarget(member);
                                }}
                              >
                                Login as User
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                height: "400px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              No user to show
            </div>
          )}
        </div>
      </div>
    </>
  );
}
